"use client";

import { ArrowLeft, MessageCircle, TrendingUp, Clock, User, ThumbsUp } from 'lucide-react';
import Link from 'next/link';
import type { ForumIcon, ForumPageContent } from '@/lib/page-content-types';

const forumCategories = [
  {
    id: 1,
    name: 'Tasting Notes & Reviews',
    description: 'Share your tasting experiences and read reviews from fellow wine lovers',
    topics: 247,
    posts: 1893,
    lastActivity: '5 minutes ago',
    icon: 'message' as ForumIcon,
    color: 'var(--oxblood)'
  },
  {
    id: 2,
    name: 'Vintage Discussion',
    description: 'Talk about specific vintages, aging potential, and drinking windows',
    topics: 189,
    posts: 1456,
    lastActivity: '23 minutes ago',
    icon: 'trend' as ForumIcon,
    color: 'var(--copper)'
  },
  {
    id: 3,
    name: 'Winery Visits & Tours',
    description: 'Share experiences from winery visits and plan tours',
    topics: 156,
    posts: 982,
    lastActivity: '1 hour ago',
    icon: 'message' as ForumIcon,
    color: 'var(--oxblood)'
  },
  {
    id: 4,
    name: 'Collecting & Cellaring',
    description: 'Tips on building collections, storage, and investment-worthy wines',
    topics: 134,
    posts: 1124,
    lastActivity: '2 hours ago',
    icon: 'trend' as ForumIcon,
    color: 'var(--copper)'
  },
  {
    id: 5,
    name: 'Food & Wine Pairing',
    description: 'Discuss perfect pairings with Middle Eastern and international cuisine',
    topics: 98,
    posts: 743,
    lastActivity: '3 hours ago',
    icon: 'message' as ForumIcon,
    color: 'var(--oxblood)'
  },
  {
    id: 6,
    name: 'New Releases',
    description: 'First to taste? Share your thoughts on the latest vintages',
    topics: 213,
    posts: 1567,
    lastActivity: '45 minutes ago',
    icon: 'trend' as ForumIcon,
    color: 'var(--copper)'
  }
];

const forumIconMap = {
  message: MessageCircle,
  trend: TrendingUp,
} as const;

const trendingTopics = [
  {
    id: 1,
    title: 'Castel Grand Vin 2017 - Is this the best Israeli vintage ever?',
    author: 'David_Collector',
    category: 'Tasting Notes & Reviews',
    replies: 34,
    views: 892,
    likes: 67,
    timeAgo: '2 hours ago',
    excerpt: 'Just opened my first bottle and I\'m blown away. The complexity, structure, and balance are on another level...'
  },
  {
    id: 2,
    title: 'Building a starter Israeli wine collection - $500 budget',
    author: 'WineNewbie23',
    category: 'Collecting & Cellaring',
    replies: 28,
    views: 654,
    likes: 45,
    timeAgo: '5 hours ago',
    excerpt: 'Looking to start my Israeli wine collection. I have $500 to spend. What should I prioritize?'
  },
  {
    id: 3,
    title: 'Judean Hills vs Golan Heights - terroir comparison',
    author: 'TerriorGeek',
    category: 'Vintage Discussion',
    replies: 42,
    views: 1203,
    likes: 89,
    timeAgo: '1 day ago',
    excerpt: 'After tasting side-by-side Cabernets from both regions, here are my observations on how terroir shapes the wines...'
  },
  {
    id: 4,
    title: 'Galil Mountain tour experience - highly recommended',
    author: 'TourEnthusiast',
    category: 'Winery Visits & Tours',
    replies: 19,
    views: 567,
    likes: 38,
    timeAgo: '1 day ago',
    excerpt: 'Just completed the premium tour at Galil Mountain. The views, hospitality, and wines were exceptional...'
  },
  {
    id: 5,
    title: 'Old vine Carignan - underrated gem of Israeli wine',
    author: 'HeritageWines',
    category: 'Tasting Notes & Reviews',
    replies: 51,
    views: 1456,
    likes: 102,
    timeAgo: '2 days ago',
    excerpt: 'Why aren\'t more people talking about old-vine Carignan? These wines offer incredible character and value...'
  },
  {
    id: 6,
    title: 'Pairing Israeli wine with shakshuka - your favorites?',
    author: 'FoodieCollector',
    category: 'Food & Wine Pairing',
    replies: 23,
    views: 489,
    likes: 31,
    timeAgo: '3 days ago',
    excerpt: 'I\'ve been experimenting with different Israeli wines alongside shakshuka. Here are my top pairings...'
  }
];

export function ForumPage({ content }: { content?: ForumPageContent | null }) {
  const forumCategoriesData = content?.forumCategories?.length ? content.forumCategories : forumCategories;
  const trendingTopicsData = content?.trendingTopics?.length ? content.trendingTopics : trendingTopics;

  return (
    <div className="min-h-screen" style={{ fontFamily: 'var(--font-body)' }}>
      {/* Back navigation */}
      <div style={{ backgroundColor: 'var(--linen)', borderBottom: '1px solid var(--stone)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16 py-8">
          <Link 
            href="/community"
            className="inline-flex items-center gap-2 transition-opacity duration-200 hover:opacity-50"
            style={{ 
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm)',
              color: 'var(--charcoal)',
              fontWeight: 400
            }}
          >
            <ArrowLeft size={16} />
            Back to Community
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-4xl">
              <h1 
                className="mb-8"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-5xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Community Forum
              </h1>
              
              <p 
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-xl)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300,
                  maxWidth: '720px'
                }}
              >
                Connect with fellow Israeli wine enthusiasts. Share tasting notes, ask questions, discuss vintages, and learn from collectors and experts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <h2 
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Forum Categories
              </h2>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {forumCategoriesData.map((category) => {
                const ForumCategoryIcon = forumIconMap[category.icon];
                return (
                <div
                  key={category.id}
                  className="p-10 cursor-pointer group transition-all duration-300 hover:shadow-lg"
                  style={{ backgroundColor: 'var(--paper)' }}
                >
                  <div className="flex items-start gap-6">
                    <div 
                      className="p-4"
                      style={{ backgroundColor: 'var(--linen)' }}
                    >
                      <ForumCategoryIcon size={24} style={{ color: category.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 
                        className="mb-2"
                        style={{ 
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--text-xl)',
                          fontWeight: 300,
                          color: 'var(--charcoal)'
                        }}
                      >
                        {category.name}
                      </h3>
                      <p 
                        className="mb-6"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          lineHeight: 'var(--line-height-body)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {category.description}
                      </p>
                      <div className="flex items-center gap-6 text-sm">
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--slate)',
                            fontWeight: 400
                          }}
                        >
                          <strong style={{ color: 'var(--charcoal)' }}>{category.topics}</strong> topics
                        </span>
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--slate)',
                            fontWeight: 400
                          }}
                        >
                          <strong style={{ color: 'var(--charcoal)' }}>{category.posts}</strong> posts
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <Clock size={14} style={{ color: 'var(--slate)' }} />
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          Last activity {category.lastActivity}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Trending Topics */}
      <section style={{ backgroundColor: 'var(--paper)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp size={24} style={{ color: 'var(--oxblood)' }} />
                <h2 
                  style={{ 
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-3xl)',
                    fontWeight: 300,
                    lineHeight: 'var(--line-height-tight)',
                    color: 'var(--charcoal)',
                    letterSpacing: '-0.02em'
                  }}
                >
                  Trending Discussions
                </h2>
              </div>
            </div>

            <div className="space-y-6">
              {trendingTopicsData.map((topic) => (
                <div
                  key={topic.id}
                  className="p-10 border cursor-pointer group transition-all duration-300 hover:shadow-lg"
                  style={{ borderColor: 'var(--stone)', backgroundColor: 'var(--linen)' }}
                >
                  <div className="flex items-start gap-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <span 
                          className="px-3 py-1"
                          style={{ 
                            backgroundColor: 'var(--olive-mist)',
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--charcoal)',
                            fontWeight: 400
                          }}
                        >
                          {topic.category}
                        </span>
                        <div className="flex items-center gap-2">
                          <User size={14} style={{ color: 'var(--slate)' }} />
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-xs)',
                              color: 'var(--slate)',
                              fontWeight: 400
                            }}
                          >
                            {topic.author}
                          </span>
                        </div>
                        <span style={{ color: 'var(--stone)' }}>•</span>
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-xs)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {topic.timeAgo}
                        </span>
                      </div>

                      <h3 
                        className="mb-3"
                        style={{ 
                          fontFamily: 'var(--font-display)',
                          fontSize: 'var(--text-xl)',
                          fontWeight: 300,
                          lineHeight: 1.3,
                          color: 'var(--charcoal)'
                        }}
                      >
                        {topic.title}
                      </h3>

                      <p 
                        className="mb-6"
                        style={{ 
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-sm)',
                          lineHeight: 'var(--line-height-body)',
                          color: 'var(--slate)',
                          fontWeight: 300
                        }}
                      >
                        {topic.excerpt}
                      </p>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <MessageCircle size={16} style={{ color: 'var(--slate)' }} />
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--charcoal)',
                              fontWeight: 400
                            }}
                          >
                            {topic.replies} replies
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ThumbsUp size={16} style={{ color: 'var(--slate)' }} />
                          <span 
                            style={{ 
                              fontFamily: 'var(--font-body)',
                              fontSize: 'var(--text-sm)',
                              color: 'var(--charcoal)',
                              fontWeight: 400
                            }}
                          >
                            {topic.likes}
                          </span>
                        </div>
                        <span 
                          style={{ 
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--slate)',
                            fontWeight: 300
                          }}
                        >
                          {topic.views} views
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <button 
                className="px-8 py-4 transition-opacity duration-200 hover:opacity-80"
                style={{ 
                  backgroundColor: 'var(--charcoal)',
                  color: 'var(--paper)',
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 400
                }}
              >
                View All Discussions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ backgroundColor: 'var(--linen)' }}>
        <div className="mx-auto max-w-screen-2xl px-8 lg:px-16">
          <div className="py-24 lg:py-32">
            <div className="max-w-3xl mx-auto text-center">
              <h2 
                className="mb-6"
                style={{ 
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-4xl)',
                  fontWeight: 300,
                  lineHeight: 'var(--line-height-tight)',
                  color: 'var(--charcoal)',
                  letterSpacing: '-0.02em'
                }}
              >
                Join the Conversation
              </h2>
              <p 
                className="mb-10"
                style={{ 
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-lg)',
                  lineHeight: 'var(--line-height-body)',
                  color: 'var(--slate)',
                  fontWeight: 300
                }}
              >
                Create an account to participate in discussions, share your tasting notes, and connect with the Israeli wine community.
              </p>
              <div className="flex gap-4 justify-center">
                <button 
                  className="px-8 py-4 transition-opacity duration-200 hover:opacity-80"
                  style={{ 
                    backgroundColor: 'var(--charcoal)',
                    color: 'var(--paper)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-base)',
                    fontWeight: 400
                  }}
                >
                  Create Account
                </button>
                <button 
                  className="px-8 py-4 transition-colors duration-200 hover:bg-[var(--paper)]"
                  style={{ 
                    border: '1px solid var(--charcoal)',
                    backgroundColor: 'transparent',
                    color: 'var(--charcoal)',
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-base)',
                    fontWeight: 400
                  }}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
